import { AuthToken } from '@cobliteam/api-lib/src/account/session/AuthToken';
import { Location, UserRole } from '@cobliteam/common-types';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useSessionStore from '@cobliteam/hooks/src/useSession/store/useSessionStore';
import useSessionQuery from '@cobliteam/hooks/src/useSession/useSessionQuery';
import useSyncUserDataSideEffects from '@cobliteam/hooks/src/useSession/useSyncUserDataSideEffects';
import type React from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import InitialLoadingPage from '../pages/LoadingPage/InitialLoadingPage';

interface PrivateRouterProviderProps {
  children: React.ReactNode;
}

const POST_LOGIN_REDIRECT_ROUTES = [Location.EMPTY, Location.LOGIN, Location.PASSWORD_RECOVERY, Location.TOKEN_VALIDATION, Location.PASSWORD_RESET, Location.MFA_CHALLENGE];

const PrivateRouterProvider = ({ children }: PrivateRouterProviderProps) => {
  const { pathname } = useLocation();
  const session = useSessionStore((s) => s.session);
  const authToken = localStorage.getItem(AuthToken.ACCESS_TOKEN);

  // Revalida sessão em background contra /account/session. O `setSession` acontece dentro da
  // queryFn — quando a resposta chega, o `useAuthStore` atualiza atomicamente e o `useUserData`
  // abaixo já enxerga a sessão fresca sem esperar uma render cycle extra.
  useSessionQuery();

  const { useGetUserData } = useUser();
  const { data: userData } = useGetUserData();

  // Sincroniza o `userData` fresco nos stores/efeitos periféricos. Único ponto de mount para
  // evitar duplicação vs. consumidores múltiplos.
  useSyncUserDataSideEffects(userData, session?.user.id, session?.fleet.id);

  if (!authToken) return <Redirect to='/login' />;

  if (session && POST_LOGIN_REDIRECT_ROUTES.includes(pathname as Location)) {
    if (session.fleet.active === false) return <Redirect to={Location.BUSINESS_MANAGER_BOLETOS} />;
    return <Redirect to={session.user.role === UserRole.MAP_VIEW ? Location.MONITORING : Location.HOME} />;
  }

  // Gate de boot: sem snapshot em localStorage (boot otimista revertido), o store começa vazio
  // e o loading fica visível até a revalidação de `/account/session` popular a sessão.
  if (session) return <>{children}</>;

  return <InitialLoadingPage />;
};

export default PrivateRouterProvider;
